import { create } from "zustand";
import { AlertColor } from "../customEnums/AlertColor";

export interface AlertItem {
    id: string;
    severity: AlertColor;
    title: string;
    message?: string;
    autoCloseTime?: number | null;
    dismissible?: boolean;
    onClose?: () => void;
}

interface AlertState {
    alerts: AlertItem[];
    enqueueAlert: (alert: Omit<AlertItem, "id">) => string;
    dismissAlert: (id: string) => void;
    clearAlerts: () => void;
    updateAlert: (id: string, update: Partial<AlertItem>) => void;
}

const getAutoCloseTime = (severity: AlertColor) => {
    if (severity === AlertColor.SUCCESS) return 5000;
    if (severity === AlertColor.INFO) return 10000;
    return null;
};

const getNextId = () => {
    return `alert_${Date.now()}`;
};

export const useToastAlertStore = create<AlertState>((set) => ({
    alerts: [],
    enqueueAlert: (alert) => {
        const alertKey = `${alert.severity}|${alert.title}|${alert.message ?? ""}`;
        const dedupeKey = alertKey.trim().toLowerCase();
        const existingAlert = useToastAlertStore
            .getState()
            .alerts
            .find((item) => `${item.severity}|${item.title}|${item.message ?? ""}`.trim().toLowerCase() === dedupeKey);
        if (existingAlert) {
            return existingAlert.id;
        }
        const id = getNextId();
        const autoCloseTime = alert.autoCloseTime === undefined
            ? getAutoCloseTime(alert.severity)
            : alert.autoCloseTime;
        const dismissible = alert.dismissible === undefined ? true : alert.dismissible;
        const newAlert: AlertItem = { ...alert, id, autoCloseTime: autoCloseTime, dismissible };
        set((state) => ({ alerts: [...state.alerts, newAlert] }));
        return id;
    },
    dismissAlert: (id) => {
        set((state) => {
            const alert = state.alerts.find((item) => item.id === id);
            if (alert?.onClose) alert.onClose();
            return { alerts: state.alerts.filter((item) => item.id !== id) };
        });
    },
    clearAlerts: () => set({ alerts: [] }),
    updateAlert: (id, update) => {
        set((state) => ({
            alerts: state.alerts.map((item) => (item.id === id ? { ...item, ...update } : item)),
        }));
    },
}));